import { ActionItemCategory } from "@elation/api-client/src/stitch-web/models/actionItemCategory";
import {
  BookOpen,
  ClipboardList,
  FlaskConical,
  type LucideIcon,
  Pill,
  StickyNote,
  UserRound,
} from "lucide-react";

export type ActionItemSection =
  | "Prescriptions"
  | "Orders"
  | "Referrals"
  | "Miscellaneous"
  | "Assessments"
  | "Patient Education";

export const sectionHeaderIconMap: Record<ActionItemSection, LucideIcon> = {
  Prescriptions: Pill,
  Orders: FlaskConical,
  Referrals: UserRound,
  Miscellaneous: StickyNote,
  Assessments: ClipboardList,
  "Patient Education": BookOpen,
};

export type ActionItemSectionTheme = {
  icon: string;
  checkbox: string;
  card: string;
  cardActive: string;
};

export const sectionTheme: Record<ActionItemSection, ActionItemSectionTheme> = {
  Prescriptions: {
    icon: "text-violet-600 dark:text-violet-400",
    checkbox:
      "border-violet-500/70 data-[state=checked]:border-violet-600 data-[state=checked]:bg-violet-600 data-[state=checked]:text-white",
    card: "border border-border border-l-[3px] border-l-violet-500/80 bg-card",
    cardActive:
      "border-violet-200/80 bg-violet-50/60 dark:border-violet-900/40 dark:bg-violet-950/30",
  },
  Orders: {
    icon: "text-sky-600 dark:text-sky-400",
    checkbox:
      "border-sky-500/70 data-[state=checked]:border-sky-600 data-[state=checked]:bg-sky-600 data-[state=checked]:text-white",
    card: "border border-border border-l-[3px] border-l-sky-500/80 bg-card",
    cardActive:
      "border-sky-200/80 bg-sky-50/60 dark:border-sky-900/40 dark:bg-sky-950/30",
  },
  Referrals: {
    icon: "text-amber-600 dark:text-amber-400",
    checkbox:
      "border-amber-500/70 data-[state=checked]:border-amber-600 data-[state=checked]:bg-amber-600 data-[state=checked]:text-white",
    card: "border border-border border-l-[3px] border-l-amber-500/80 bg-card",
    cardActive:
      "border-amber-200/80 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/30",
  },
  Miscellaneous: {
    icon: "text-slate-500 dark:text-slate-400",
    checkbox:
      "border-slate-400/80 data-[state=checked]:border-slate-600 data-[state=checked]:bg-slate-600 data-[state=checked]:text-white",
    card: "border border-border border-l-[3px] border-l-slate-400/70 bg-card",
    cardActive:
      "border-slate-200/80 bg-slate-50/70 dark:border-slate-700 dark:bg-slate-900/40",
  },
  Assessments: {
    icon: "text-emerald-600 dark:text-emerald-400",
    checkbox:
      "border-emerald-500/70 data-[state=checked]:border-emerald-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:text-white",
    card: "border border-border border-l-[3px] border-l-emerald-500/80 bg-card",
    cardActive:
      "border-emerald-200/80 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/30",
  },
  "Patient Education": {
    icon: "text-indigo-600 dark:text-indigo-400",
    checkbox:
      "border-indigo-500/70 data-[state=checked]:border-indigo-600 data-[state=checked]:bg-indigo-600 data-[state=checked]:text-white",
    card: "border border-border border-l-[3px] border-l-indigo-500/80 bg-card",
    cardActive:
      "border-indigo-200/80 bg-indigo-50/60 dark:border-indigo-900/40 dark:bg-indigo-950/30",
  },
};

export const categoryToSectionHeader = {
  [ActionItemCategory.Medication_Start]: "Prescriptions",
  [ActionItemCategory.Medication_Discontinuation]: "Prescriptions",
  [ActionItemCategory.Medication_Refill]: "Prescriptions",
  [ActionItemCategory.Medication_Details_Change]: "Prescriptions",
  [ActionItemCategory.Medication_Continue]: "Prescriptions",
  [ActionItemCategory.Lab_Order]: "Orders",
  [ActionItemCategory.Imaging_Order]: "Orders",
  [ActionItemCategory.Medical_Referral]: "Referrals",
  [ActionItemCategory.Other_Action]: "Miscellaneous",
  [ActionItemCategory.Document_Review]: "Assessments",
  [ActionItemCategory.Advise_Patient]: "Patient Education",
  [ActionItemCategory.Take_Note]: "Miscellaneous",
  [ActionItemCategory.Physical_Exam]: "Assessments",
  [ActionItemCategory.Symptom_Evaluation]: "Assessments",
} as const;
